/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { notifications } from '@utils/notifications';
import Config from '@root/wrapperConfig';
import type { DocumentNode } from 'graphql';
import GraphQL from './graphql';
import AuthQL from './gql/queryMain/auth.mainEndpoint';
import { CartQL } from './gql/queryMain/cart.mainEndpoint';
import type * as MainTypes from './schema/main';
import PersonalAccMain from './gql/queryMain/personalAcc.mainEndpoint';
import { getLocalStorageItem } from '../utils/getLocalStorageItem';
import type { GqlResponse } from '../types';
import ShopsQuery from './gql/queryMain/shops.mainEndpoint';

function prepareParams<T>(data: T, fields: string[] = []) {
  if (typeof data === 'undefined' || data === null) {
    return data;
  }

  const responseData = { ...data };
  if (fields.length) {
    fields.forEach(a => {
      if (responseData) {
        if (typeof responseData[a] === 'undefined') {
          return;
        }
        responseData[a] = parseInt(responseData[a], 10);
      }
    });
  }

  return responseData;
}

export default new (class {
  // Only GraphQL
  server: string;

  proxyurl: string;

  notificationQueue: Set<string>;

  constructor() {
    this.server = Config.server.api;
    this.proxyurl = '';
    this.notificationQueue = new Set<string>();
  }

  // Promocode
  applyPromoCode = async (
    variables: MainTypes.ApplyPromoCodeMutationVariables
  ): Promise<MainTypes.ApplyPromoCodeMutation> =>
    await GraphQL.getPromocodeInfo(variables).then(async res => {
      if (
        res?.getPromocodeInfo?.promocodeItem?.is_mobile_only === 1 &&
        res?.getPromocodeInfo?.success
      ) {
        const message =
          'Данный промокод предназначен только для мобильного приложения.';

        notifications(message, 'Внимание');
        const errorRes: MainTypes.ApplyPromoCodeMutation = {
          applyPromoCode: {
            cart: null,
            error: message,
            errorCode: null,
            identify: null,
            success: false
          }
        };
        return errorRes;
      }

      return await this.callMethod<
        MainTypes.ApplyPromoCodeMutation,
        MainTypes.ApplyPromoCodeMutationVariables
      >(CartQL.applyPromoCode, variables);
    });

  getCartToken = async (cookieId?: string) =>
    await this.callMethod<MainTypes.MeQuery, MainTypes.MeQueryVariables>(
      AuthQL.getCartToken,
      {},
      cookieId
    );

  removePeopleAddress = async (
    variables: MainTypes.RemovePeopleAddressMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.RemovePeopleAddressMutation,
      MainTypes.RemovePeopleAddressMutationVariables
    >(AuthQL.removePeopleAddress, variables);

  addPeopleAddress = async (
    variables: MainTypes.AddPeopleAddressMutationVariables
  ) => {
    variables.data.dadata = Number(variables?.data?.dadata);
    return await this.callMethod<
      MainTypes.AddPeopleAddressMutation,
      MainTypes.AddPeopleAddressMutationVariables
    >(AuthQL.addPeopleAddress, variables);
  };

  updatePeople = async (variables: MainTypes.UpdatePeopleMutationVariables) =>
    await this.callMethod<
      MainTypes.UpdatePeopleMutation,
      MainTypes.UpdatePeopleMutationVariables
    >(AuthQL.updatePeople, variables);

  updatePeopleAddress = async (
    variables: MainTypes.UpdatePeopleAddressMutationVariables
  ) => {
    variables.data.dadata = Number(variables?.data?.dadata);
    return await this.callMethod<
      MainTypes.UpdatePeopleAddressMutation,
      MainTypes.UpdatePeopleAddressMutationVariables
    >(AuthQL.updatePeopleAddress, variables);
  };

  cart = async (variables: MainTypes.CartQueryVariables) =>
    await this.callMethod<MainTypes.CartQuery, MainTypes.CartQueryVariables>(
      CartQL.cart,
      variables
    );

  updateOrCreateMenuCart = async (
    variables: MainTypes.UpdateOrCreateMenuCartMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.UpdateOrCreateMenuCartMutation,
      MainTypes.UpdateOrCreateMenuCartMutationVariables
    >(CartQL.updateOrCreateMenuCart, variables);

  createMenuOrderFromCart = async (
    variables: MainTypes.CreateMenuOrderFromCartMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.CreateMenuOrderFromCartMutation,
      MainTypes.CreateMenuOrderFromCartMutationVariables
    >(CartQL.createMenuOrderFromCart, variables);

  addMultipleProductsToCart = async (
    variables: MainTypes.AddMultipleProductsToCartMutationVariables,
    isDisabledNotification?: boolean
  ) =>
    await this.callMethod<
      MainTypes.AddMultipleProductsToCartMutation,
      MainTypes.AddMultipleProductsToCartMutationVariables
    >(CartQL.addMultipleProductsToCart, variables, '', isDisabledNotification);

  addProductToCart = async (
    variables: MainTypes.AddProductToCartMutationVariables,
    isDisabledNotification?: boolean
  ) =>
    await this.callMethod<
      MainTypes.AddProductToCartMutation,
      MainTypes.AddProductToCartMutationVariables
    >(
      CartQL.addProductToCart,
      prepareParams<MainTypes.AddProductToCartMutationVariables>(variables, [
        'productId',
        'quantity',
        'cartItemId'
      ]),
      '',
      isDisabledNotification
    );

  removeCartItem = async (
    variables: MainTypes.RemoveCartItemMutationVariables,
    isDisabledNotification?: boolean
  ) =>
    await this.callMethod<
      MainTypes.RemoveCartItemMutation,
      MainTypes.RemoveCartItemMutationVariables
    >(CartQL.removeCartItem, variables, '', isDisabledNotification);

  addProductToPostponed = async (
    variables: MainTypes.AddProductToPostponedListMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.AddProductToPostponedListMutation,
      MainTypes.AddProductToPostponedListMutationVariables
    >(CartQL.addProductToPostponed, variables);

  removeProductFromPostponed = async (
    variables: MainTypes.RemoveProductFromPostponedListMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.RemoveProductFromPostponedListMutation,
      MainTypes.RemoveProductFromPostponedListMutationVariables
    >(CartQL.removeProductFromPostponed, variables);

  clearCart = async (variables: MainTypes.ClearCartMutationVariables) =>
    await this.callMethod<
      MainTypes.ClearCartMutation,
      MainTypes.ClearCartMutationVariables
    >(CartQL.clearCart, variables);

  removeCartService = async (
    variables: MainTypes.RemoveCartServiceMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.RemoveCartServiceMutation,
      MainTypes.RemoveCartServiceMutationVariables
    >(CartQL.removeCartService, variables);

  addServiceToCart = async (
    variables: MainTypes.AddServiceToCartMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.AddServiceToCartMutation,
      MainTypes.AddServiceToCartMutationVariables
    >(CartQL.addServiceToCart, variables);

  payWithBonuses = async (
    variables: MainTypes.PayOrderWithBonusesMutationVariables
  ) => {
    variables.amount = +variables?.amount;
    return await this.callMethod<
      MainTypes.PayOrderWithBonusesMutation,
      MainTypes.PayOrderWithBonusesMutationVariables
    >(CartQL.payWithBonuses, variables);
  };

  removeCartPromoCode = async (
    variables: MainTypes.RemoveCartPromoCodeMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.RemoveCartPromoCodeMutation,
      MainTypes.RemoveCartPromoCodeMutationVariables
    >(CartQL.removeCartPromoCode, variables);

  createDigitalOrder = async (
    variables: MainTypes.CreateDigitalOrderMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.CreateDigitalOrderMutation,
      MainTypes.CreateDigitalOrderMutationVariables
    >(CartQL.createDigitalOrder, variables);

  applicationForParticipationInTestDrive = async (
    variables: MainTypes.ApplicationForParticipationInTestDriveMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.ApplicationForParticipationInTestDriveMutation,
      MainTypes.ApplicationForParticipationInTestDriveMutationVariables
    >(CartQL.applicationForParticipationInTestDrive, variables);

  cancelTestDriveApplication = async variables =>
    await this.callMethod<
      MainTypes.CancelTestDriveApplicationMutation,
      MainTypes.CancelTestDriveApplicationMutationVariables
    >(CartQL.cancelTestDriveApplication, variables);

  shareCart = async (variables: MainTypes.ShareCartQueryVariables) =>
    await this.callMethod<
      MainTypes.ShareCartQuery,
      MainTypes.ShareCartQueryVariables
    >(CartQL.shareCart, variables);

  addCartItemPostcard = async (
    variables: MainTypes.AddCartItemPostcardMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.AddCartItemPostcardMutation,
      MainTypes.AddCartItemPostcardMutationVariables
    >(CartQL.addCartItemPostcard, variables);

  clearCookie = async () =>
    await this.callMethod<MainTypes.ClearCookieQuery>(CartQL.clearCookie);

  getReplacedProductCart = async (
    variables: MainTypes.GetReplacedProductCartQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.GetReplacedProductCartQuery,
      MainTypes.GetReplacedProductCartQueryVariables
    >(CartQL.getReplacedProductCart, variables);

  getOrderCancelReasons = async () =>
    await this.callMethod<
      MainTypes.GetOrderCancelReasonsQuery,
      MainTypes.GetOrderCancelReasonsQueryVariables
    >(CartQL.getOrderCancelReasons);

  myNotificationsList = async (
    variables: MainTypes.MyNotificationsListQueryVariables
  ) =>
    await this.callMethod<
      MainTypes.MyNotificationsListQuery,
      MainTypes.MyNotificationsListQueryVariables
    >(PersonalAccMain.myNotificationsList, variables);

  notCheckedNotificationsAmount = async () =>
    await this.callMethod<MainTypes.NotCheckedNotificationsAmountQuery>(
      PersonalAccMain.notCheckedNotificationsAmount
    );

  subscribeToPushNotifications = async (
    variables: MainTypes.SubscribeToPushNotificationsMutationVariables
  ) =>
    await this.callMethod<
      MainTypes.SubscribeToPushNotificationsMutation,
      MainTypes.SubscribeToPushNotificationsMutationVariables
    >(PersonalAccMain.subscribeToPushNotifications, variables);

  getShops = async () =>
    await this.callMethod<MainTypes.GetShopsQuery>(ShopsQuery.getShops);

  getShops2 = async () =>
    await this.callMethod<MainTypes.GetShops2Query>(ShopsQuery.getShops2);

  getActiveStoreCityWithFields = async () =>
    await this.callMethod<MainTypes.GetActiveStoreCityWithFieldsQuery>(
      CartQL.getActiveStoreCityWithFields
    );

  async callMethod<T, K = void>(
    query: DocumentNode,
    variables?: K,
    cookieId?: string,
    isDisabledNotification = false
  ): Promise<T> {
    const headerToken = cookieId || getLocalStorageItem<string>('headerToken');
    const xStoreId = getLocalStorageItem<string>('x-store-id');
    return await new Promise((resolve, reject) => {
      fetch(this.proxyurl + this.server, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-People-Token': headerToken,
          'x-store-id': xStoreId,
          'x-request-id': `${Date.now()}::${Math.trunc(Math.random() * 10000)}`
        },
        body: JSON.stringify({
          query: query.loc?.source.body,
          variables
        })
      })
        .then(async response => await response.json())
        .then(async (data: GqlResponse<T>) => {
          let message = '';

          if (typeof data.data !== 'undefined') {
            for (const prop in data.data) {
              const methodProp = data.data[prop];
              if (
                methodProp &&
                typeof methodProp.warning !== 'undefined' &&
                methodProp.warning != null &&
                methodProp.warning.length !== 0
              ) {
                message = methodProp.warning;
              }

              if (
                methodProp &&
                typeof methodProp.error !== 'undefined' &&
                methodProp.error != null &&
                methodProp.error.length !== 0
              ) {
                message = methodProp.error;
              }
            }
            if (data?.data?.removeCartItem) {
              this.notificationQueue.clear();
            }
            if (message.length !== 0 && !isDisabledNotification) {
              if (!this.notificationQueue.has(message)) {
                this.notificationQueue.add(message);
                notifications(message, 'Внимание');
              }
            }
          }

          if (
            typeof data.data === 'undefined' ||
            data.data === null ||
            typeof data.errors !== 'undefined'
          ) {
            return await Promise.reject(data.errors);
          }
          resolve(data.data);
        })
        .catch(e => {
          notifications(e);
          reject(e);
        });
    });
  }
})();
